import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { pluck, shareReplay, take, throttleTime } from 'rxjs/operators';
import { AuthService } from '../root/auth.service';
import { FeatureFlagService } from '../root/feature-flags.service';
import { SnackBarService } from '../shared/snackbar.service';
import { DriverShiftOverviewResponse } from '../types';
import { isHttpErrorResponse } from '../utilities/isHttpErrorResponse';

export interface Waypoint {
  id: number;
  longitude: number;
  latitude: number;
  timestamp: string;
  accuracy: number;
  speed: number;
  createdAt: string;
}

export interface DispatchEvent {
  createdAt: string;
  orderId: number;
}

export interface LoHiShiftEditHOSEntry {
  originallyEndedAt: string;
  nowEndsAt: string;
  createdAt: string;
}

export interface ShiftOrderDetails {
  id: number;
  status: string;
  dispatchedAt: string;
  acceptedAt: string;
  endedAt: string;
  fracName: string;
  fracLng: number;
  fracLat: number;
  fracRadius: number;
  mineName: string;
  mineLng: number;
  mineLat: number;
  mineRadius: number;
  manuallyCompleted: boolean;
  minutesToDriverAccept: number;
  minutesToMineArrival: number;
  minutesAtMine: number;
  minutesLineHaul: number;
  minutesAtFrac: number;
  minutesPastEstimatedMineArrival: number;
  minutesPastEstimatedMineDeparture: number;
  minutesPastEstimatedFracArrival: number;
  minutesPastEstimatedCompletion: number;
  totalMinutesReal: number;
  totalMinutesEstimated: number;
}

export interface ShiftInfo {
  id: number;
  startedAt: string;
  checkedInAt: string;
  endedAt: string;
  archivedAt: string;
  hoursOnShift: number;
  checkedInRadius: number;
  startLng: number;
  startLat: number;
  driverPhone: string;
  driverName: string;
}

export interface SmsMessage {
  message: string;
  type: string;
  sentAt: string;
}

export interface HazardReported {
  comment: string;
  likeCount: number;
  location: { longitude: number; latitude: number };
  positionInLine: string;
  reportedAt: string;
  resolvedAt: string;
  trucksInLine: string;
  type: string;
}

export interface HazardLiked {
  likedAt: string;
  comment: string;
  likeCount: number;
  location: { longitude: number; latitude: number };
  positionInLine: string;
  reportedAt: string;
  resolvedAt: string;
  trucksInLine: string;
  type: string;
}

export interface DriverPause {
  createdAt: string;
  endTimestamp: string;
  isEnded: boolean;
}

export interface InternalMappingData {
  waypoints: Waypoint[];
  dispatchEvents: DispatchEvent[];
  hosEdits: LoHiShiftEditHOSEntry[];
  orders: ShiftOrderDetails[];
  shiftInfo: ShiftInfo;
  smsHistory: SmsMessage[];
  hazardsReported: HazardReported[];
  hazardsLiked: HazardLiked[];
  pauses: DriverPause[];
}

@Injectable({
  providedIn: 'root',
})
export class ShiftService {
  private driversOnShift$$ = new BehaviorSubject<DriverShiftOverviewResponse>(null);
  private driversOnShiftThrottle$$ = new Subject();
  private driversOnShiftShared$ = this.driversOnShift$$.pipe(shareReplay(1));

  public get driversOnShift$(): Observable<DriverShiftOverviewResponse> {
    this.driversOnShiftThrottle$$.next();
    return this.driversOnShiftShared$;
  }

  private mapInfo$$ = new BehaviorSubject<InternalMappingData>(null);
  public mapInfo$ = this.mapInfo$$.asObservable().pipe(shareReplay(1));

  constructor(
    private httpClient: HttpClient,
    private logger: NGXLogger,
    private snackBar: SnackBarService,
    private featureFlagService: FeatureFlagService,
    private authService: AuthService,
  ) {
    this.driversOnShiftThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriversOnShift();
    });
  }

  public async loadMapInfo(shiftId: number) {
    try {
      const result = await this.httpClient
        .get<InternalMappingData>(`${environment.api}/v1/internal/shift_mapping/${shiftId}`)
        .toPromise();

      this.mapInfo$$.next(result);
    } catch (error) {
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error?.error?.error?.message);
      } else {
        this.snackBar.showError('Failed to load data');
      }
      this.logger.error(error);
    }
  }

  public async loadMapInfoByOrder(orderId: string) {
    const earningsProgram = await this.authService.userInfo$.pipe(take(1), pluck('earningsProgramMember')).toPromise();
    const superhighway = await this.featureFlagService.isFlagActive$('superhighway-timeline').pipe(take(1)).toPromise();
    try {
      const result = await this.httpClient
        .get<InternalMappingData>(
          earningsProgram && superhighway
            ? `${environment.api}/v2/load/${orderId}/timeline`
            : `${environment.api}/v1/admin/shift_mapping_by_order/${orderId}`,
        )
        .toPromise();

      this.mapInfo$$.next(result);
    } catch (error) {
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error?.error?.error?.message);
      } else {
        this.snackBar.showError('Failed to load data');
      }
      this.logger.error(error);
    }
  }

  public async loadMapInfoByShift(orderId: number) {
    try {
      const result = await this.httpClient
        .get<InternalMappingData>(`${environment.api}/v1/admin/shift_mapping_by_shift/${orderId}`)
        .toPromise();

      this.mapInfo$$.next(result);
    } catch (error) {
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error?.error?.error?.message);
      } else {
        this.snackBar.showError('Failed to load data');
      }
      this.logger.error(error);
    }
  }

  private async loadDriversOnShift() {
    try {
      const result = await this.httpClient
        .get<DriverShiftOverviewResponse>(`${environment.api}/v1/internal/shifts/overview`)
        .toPromise();
      this.driversOnShift$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  public loadDriversOnShiftFF(fiveFOnly: boolean) {
    try {
      if (fiveFOnly) {
        return this.httpClient.get<DriverShiftOverviewResponse>(
          `${environment.api}/v1/admin/company_driver_shifts_overview?isff=${fiveFOnly}`,
        );
      } else {
        return this.httpClient.get<DriverShiftOverviewResponse>(
          `${environment.api}/v1/internal/shifts/overview?isFF=${fiveFOnly}`,
        );
      }
    } catch (error) {
      this.logger.error(error);
    }
  }
}
